import { useState, useMemo } from "react";
import { SearchInput } from "../atoms/SearchInput";
import { FilterButton } from "../atoms/FilterButton";
import { PageHeader } from "../molecules/PageHeader";
import { SectionHeader } from "../molecules/SectionHeader";
import { ReportedItemCard } from "../molecules/ReportedItemCard";
import { DataTable } from "../organisms/DataTable";
import { StatsGrid } from "../organisms/StatsGrid";
import { CreateGroupModal } from "../organisms/CreateGroupModal";
import { Add, More } from "iconsax-react";

import {
  GROUP_STATS,
  MOCK_GROUPS,
  MOCK_REPORTED_GROUPS,
  GROUP_TABLE_HEADERS,
  type Group,
} from "@/data";

const GroupTableRow = ({ group }: { group: Group }) => (
  <tr className="border-b border-dark-30 hover:bg-dark-30 transition-colors">
    <td className="px-6 py-4 text-dark-text font-medium">{group.name}</td>
    <td className="px-6 py-4 text-dark-text">{group.creator}</td>
    <td className="px-6 py-4 text-dark-text">
      {group.type}
      {group.price && <span className="text-dark-100"> (${group.price})</span>}
    </td>
    <td className="px-6 py-4 text-dark-text">{group.members}</td>
    <td className="px-6 py-4 text-dark-text">${group.revenue}</td>
    <td className="px-6 py-4 text-dark-text">{group.location}</td>
    <td className="px-6 py-4 text-dark-text">{group.createdOn}</td>
    <td className="px-6 py-4">
      <span className="inline-flex px-2 py-1 rounded text-sm text-dark-text">
        {group.status}
      </span>
    </td>
    <td className="px-6 py-4">
      <button className="text-dark-100 hover:text-dark-text transition-colors p-1">
        <More className="w-6 h-6" color="#EEEEEE" />
      </button>
    </td>
  </tr>
);

const GroupManagement = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);

  const handleCreateGroup = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => setShowModal(false);

  // Filter groups based on search term
  const filteredGroups = useMemo(() => {
    if (!searchTerm) return MOCK_GROUPS;
    return MOCK_GROUPS.filter((group) =>
      group.id.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm]);

  return (
    <div className="max-w-7xl mx-auto space-y-10">
      <PageHeader title="Group Management" />

      <StatsGrid stats={GROUP_STATS} columns={3} />

      <DataTable
        title="Group Table"
        headers={GROUP_TABLE_HEADERS}
        data={filteredGroups}
        renderRow={(group) => <GroupTableRow key={group.id} group={group} />}
        filters={
          <>
            <FilterButton label="Type" />
            <FilterButton label="Status" />
            <FilterButton label="Date Created" />
            <FilterButton label="Location" />
            <SearchInput value={searchTerm} onChange={setSearchTerm} />
          </>
        }
        actions={
          <button
            onClick={handleCreateGroup}
            className="flex items-center gap-1.5 px-4 py-2 bg-white hover:bg-gray-100 rounded-[5px] text-dark-5 font-medium transition-colors cursor-pointer"
          >
            <Add color="#111111" className="w-5 h-5" />
            <span className="text-sm font-medium">Create New Group</span>
          </button>
        }
        currentPage={currentPage}
        onPageChange={setCurrentPage}
      />

      <CreateGroupModal isOpen={showModal} onClose={handleCloseModal} />

      <div>
        <SectionHeader title="Reported Groups" />
        <div className="space-y-2">
          {MOCK_REPORTED_GROUPS.map((group, index) => (
            <ReportedItemCard
              key={index}
              name={group.name}
              reports={group.reports}
              lastReported={group.lastReported}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default GroupManagement;
